import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { combineLatest, take } from 'rxjs';

import { allWidgetDataQueriesFilter } from 'src/app/api/queries/widget.queries';

import { toggleDarkMode } from '../state/core.actions';
import { selectCoreDarkMode, selectUser } from '../state';
import { UserApiService } from 'src/app/api/services/user-api.service';

/**
 * The "persist a preference" owners: flip the client state first (the reducer is
 * the UI), then persist - localStorage always, remotely when a user is present.
 * Persistence failures are silent by design; the preference still applied locally
 * and re-syncs on the next successful save.
 */
@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private readonly store = inject(Store);
  private readonly queryClient = inject(QueryClient);
  private readonly userApi = inject(UserApiService);

  toggleDarkMode(): void {
    this.store.dispatch(toggleDarkMode());

    combineLatest([
      this.store.select(selectCoreDarkMode),
      this.store.select(selectUser),
    ])
      .pipe(take(1))
      .subscribe(([darkMode, user]) => {
        localStorage.setItem('darkMode', JSON.stringify(darkMode));

        if (!user) {
          // No user (login screen): local only, and no widget data to repaint.
          return;
        }

        this.userApi.setDarkMode(darkMode).subscribe({
          // * Dark mode is not part of the widget-data query key: the backend
          // * renders the chart with the user's stored preference, so the data
          // * is invalidated only after the server has it.
          next: () =>
            this.queryClient.invalidateQueries(allWidgetDataQueriesFilter()),
          error: (error) => console.error('Failed to persist dark mode', error),
        });
      });
  }
}
